import { score } from 'monaco-editor/esm/vs/editor/common/languageSelector.js';
import { LanguageSelector } from '../vscode/vs/workbench/api/common/extHostTypeConverters.js';
import { getExtHostServices } from './extHost.js';
function checkSelector(selector) {
    return selector;
}
function create(getExtension) {
    return {
        createDiagnosticCollection(name) {
            const { extHostDiagnostics } = getExtHostServices();
            const extension = getExtension();
            return extHostDiagnostics.createDiagnosticCollection(extension.identifier, name);
        },
        get onDidChangeDiagnostics() {
            const { extHostDiagnostics } = getExtHostServices();
            return extHostDiagnostics.onDidChangeDiagnostics;
        },
        getDiagnostics: (resource) => {
            const { extHostDiagnostics } = getExtHostServices();
            return extHostDiagnostics.getDiagnostics(resource);
        },
        getLanguages() {
            const { extHostLanguages } = getExtHostServices();
            return extHostLanguages.getLanguages();
        },
        setTextDocumentLanguage(document, languageId) {
            const { extHostLanguages } = getExtHostServices();
            return extHostLanguages.changeLanguage(document.uri, languageId);
        },
        match(selector, document) {
            return score(LanguageSelector.from(selector), document.uri, document.languageId, true, undefined, undefined);
        },
        registerCodeActionsProvider(selector, provider, metadata) {
            const { extHostLanguageFeatures } = getExtHostServices();
            const extension = getExtension();
            return extHostLanguageFeatures.registerCodeActionProvider(extension, checkSelector(selector), provider, metadata);
        },
        registerCodeLensProvider(selector, provider) {
            const { extHostLanguageFeatures } = getExtHostServices();
            const extension = getExtension();
            return extHostLanguageFeatures.registerCodeLensProvider(extension, checkSelector(selector), provider);
        },
        registerDefinitionProvider(selector, provider) {
            const { extHostLanguageFeatures } = getExtHostServices();
            const extension = getExtension();
            return extHostLanguageFeatures.registerDefinitionProvider(extension, checkSelector(selector), provider);
        },
        registerDeclarationProvider(selector, provider) {
            const { extHostLanguageFeatures } = getExtHostServices();
            const extension = getExtension();
            return extHostLanguageFeatures.registerDeclarationProvider(extension, checkSelector(selector), provider);
        },
        registerImplementationProvider(selector, provider) {
            const { extHostLanguageFeatures } = getExtHostServices();
            const extension = getExtension();
            return extHostLanguageFeatures.registerImplementationProvider(extension, checkSelector(selector), provider);
        },
        registerTypeDefinitionProvider(selector, provider) {
            const { extHostLanguageFeatures } = getExtHostServices();
            const extension = getExtension();
            return extHostLanguageFeatures.registerTypeDefinitionProvider(extension, checkSelector(selector), provider);
        },
        registerHoverProvider(selector, provider) {
            const { extHostLanguageFeatures } = getExtHostServices();
            const extension = getExtension();
            return extHostLanguageFeatures.registerHoverProvider(extension, checkSelector(selector), provider, extension.identifier);
        },
        registerEvaluatableExpressionProvider(selector, provider) {
            const { extHostLanguageFeatures } = getExtHostServices();
            const extension = getExtension();
            return extHostLanguageFeatures.registerEvaluatableExpressionProvider(extension, checkSelector(selector), provider, extension.identifier);
        },
        registerInlineValuesProvider(selector, provider) {
            const { extHostLanguageFeatures } = getExtHostServices();
            const extension = getExtension();
            return extHostLanguageFeatures.registerInlineValuesProvider(extension, checkSelector(selector), provider, extension.identifier);
        },
        registerDocumentHighlightProvider(selector, provider) {
            const { extHostLanguageFeatures } = getExtHostServices();
            const extension = getExtension();
            return extHostLanguageFeatures.registerDocumentHighlightProvider(extension, checkSelector(selector), provider);
        },
        registerLinkedEditingRangeProvider(selector, provider) {
            const { extHostLanguageFeatures } = getExtHostServices();
            const extension = getExtension();
            return extHostLanguageFeatures.registerLinkedEditingRangeProvider(extension, checkSelector(selector), provider);
        },
        registerReferenceProvider(selector, provider) {
            const { extHostLanguageFeatures } = getExtHostServices();
            const extension = getExtension();
            return extHostLanguageFeatures.registerReferenceProvider(extension, checkSelector(selector), provider);
        },
        registerRenameProvider(selector, provider) {
            const { extHostLanguageFeatures } = getExtHostServices();
            const extension = getExtension();
            return extHostLanguageFeatures.registerRenameProvider(extension, checkSelector(selector), provider);
        },
        registerDocumentSymbolProvider(selector, provider, metadata) {
            const { extHostLanguageFeatures } = getExtHostServices();
            const extension = getExtension();
            return extHostLanguageFeatures.registerDocumentSymbolProvider(extension, checkSelector(selector), provider, metadata);
        },
        registerWorkspaceSymbolProvider(provider) {
            const { extHostLanguageFeatures } = getExtHostServices();
            const extension = getExtension();
            return extHostLanguageFeatures.registerWorkspaceSymbolProvider(extension, provider);
        },
        registerDocumentFormattingEditProvider(selector, provider) {
            const { extHostLanguageFeatures } = getExtHostServices();
            const extension = getExtension();
            return extHostLanguageFeatures.registerDocumentFormattingEditProvider(extension, checkSelector(selector), provider);
        },
        registerDocumentRangeFormattingEditProvider(selector, provider) {
            const { extHostLanguageFeatures } = getExtHostServices();
            const extension = getExtension();
            return extHostLanguageFeatures.registerDocumentRangeFormattingEditProvider(extension, checkSelector(selector), provider);
        },
        registerOnTypeFormattingEditProvider(selector, provider, firstTriggerCharacter, ...moreTriggerCharacters) {
            const { extHostLanguageFeatures } = getExtHostServices();
            const extension = getExtension();
            return extHostLanguageFeatures.registerOnTypeFormattingEditProvider(extension, checkSelector(selector), provider, [firstTriggerCharacter].concat(moreTriggerCharacters));
        },
        registerDocumentSemanticTokensProvider(selector, provider, legend) {
            const { extHostLanguageFeatures } = getExtHostServices();
            const extension = getExtension();
            return extHostLanguageFeatures.registerDocumentSemanticTokensProvider(extension, checkSelector(selector), provider, legend);
        },
        registerDocumentRangeSemanticTokensProvider(selector, provider, legend) {
            const { extHostLanguageFeatures } = getExtHostServices();
            const extension = getExtension();
            return extHostLanguageFeatures.registerDocumentRangeSemanticTokensProvider(extension, checkSelector(selector), provider, legend);
        },
        registerSignatureHelpProvider(selector, provider, firstItem, ...remaining) {
            const { extHostLanguageFeatures } = getExtHostServices();
            const extension = getExtension();
            if (typeof firstItem === 'object') {
                return extHostLanguageFeatures.registerSignatureHelpProvider(extension, checkSelector(selector), provider, firstItem);
            }
            return extHostLanguageFeatures.registerSignatureHelpProvider(extension, checkSelector(selector), provider, typeof firstItem === 'undefined' ? [] : [firstItem, ...remaining]);
        },
        registerCompletionItemProvider(selector, provider, ...triggerCharacters) {
            const { extHostLanguageFeatures } = getExtHostServices();
            const extension = getExtension();
            return extHostLanguageFeatures.registerCompletionItemProvider(extension, checkSelector(selector), provider, triggerCharacters);
        },
        registerDocumentLinkProvider(selector, provider) {
            const { extHostLanguageFeatures } = getExtHostServices();
            const extension = getExtension();
            return extHostLanguageFeatures.registerDocumentLinkProvider(extension, checkSelector(selector), provider);
        },
        registerColorProvider(selector, provider) {
            const { extHostLanguageFeatures } = getExtHostServices();
            const extension = getExtension();
            return extHostLanguageFeatures.registerColorProvider(extension, checkSelector(selector), provider);
        },
        registerFoldingRangeProvider(selector, provider) {
            const { extHostLanguageFeatures } = getExtHostServices();
            const extension = getExtension();
            return extHostLanguageFeatures.registerFoldingRangeProvider(extension, checkSelector(selector), provider);
        },
        registerSelectionRangeProvider(selector, provider) {
            const { extHostLanguageFeatures } = getExtHostServices();
            const extension = getExtension();
            return extHostLanguageFeatures.registerSelectionRangeProvider(extension, selector, provider);
        },
        registerCallHierarchyProvider(selector, provider) {
            const { extHostLanguageFeatures } = getExtHostServices();
            const extension = getExtension();
            return extHostLanguageFeatures.registerCallHierarchyProvider(extension, selector, provider);
        },
        registerTypeHierarchyProvider(selector, provider) {
            const { extHostLanguageFeatures } = getExtHostServices();
            const extension = getExtension();
            return extHostLanguageFeatures.registerTypeHierarchyProvider(extension, selector, provider);
        },
        setLanguageConfiguration: (language, configuration) => {
            const { extHostLanguageFeatures } = getExtHostServices();
            const extension = getExtension();
            return extHostLanguageFeatures.setLanguageConfiguration(extension, language, configuration);
        },
        registerInlayHintsProvider(selector, provider) {
            const { extHostLanguageFeatures } = getExtHostServices();
            const extension = getExtension();
            return extHostLanguageFeatures.registerInlayHintsProvider(extension, selector, provider);
        },
        createLanguageStatusItem(id, selector) {
            const { extHostLanguages } = getExtHostServices();
            const extension = getExtension();
            return extHostLanguages.createLanguageStatusItem(extension, id, selector);
        },
        registerInlineCompletionItemProvider(selector, provider) {
            const { extHostLanguageFeatures } = getExtHostServices();
            const extension = getExtension();
            return extHostLanguageFeatures.registerInlineCompletionsProvider(extension, checkSelector(selector), provider);
        },
        registerDocumentDropEditProvider(selector, provider) {
            const { extHostLanguageFeatures } = getExtHostServices();
            const extension = getExtension();
            return extHostLanguageFeatures.registerDocumentOnDropEditProvider(extension, selector, provider);
        }
    };
}
export { create as default };
